import React, { useEffect, useState, useRef } from "react";
import { Slider } from "@mhome/ui";
import { CirclePower } from "lucide-react";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import BrightnessBar from "../common/brightness-bar";
import {
  kelvinToRGB,
  hexToHsl,
  rgbToHsl,
  getDisplayColor,
} from "../common/color-util";
import { useComponentBackgroundColor } from "../../common/adaptive-theme-provider";
import { Typography } from "@mhome/ui";

// Compute a readable foreground color (black/white) based on background color lightness
const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor) return "#333333";
  try {
    const color = String(backgroundColor).trim();
    // If HSL string, parse the lightness
    if (color.startsWith("hsl")) {
      const match = color.match(
        /hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/i
      );
      if (match) {
        const l = parseFloat(match[3]);
        return l > 90 ? "#333333" : "#FFFFFF";
      }
    }
    // If hex, convert to HSL
    if (color.startsWith("#")) {
      const { l } = hexToHsl(color);
      return l > 90 ? "#333333" : "#FFFFFF";
    }
    // If rgb(), convert to HSL
    if (color.startsWith("rgb")) {
      const { l } = rgbToHsl(color);
      return l > 90 ? "#333333" : "#FFFFFF";
    }
  } catch (e) {
    // pass
  }
  return "#FFFFFF";
};

const FAVORITE_COLORS_KEY = "colorDialog1_favoriteColors";

const getFavoriteColors = () => {
  try {
    const stored = localStorage.getItem(FAVORITE_COLORS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const saveFavoriteColor = (color, mode) => {
  try {
    const favorites = getFavoriteColors();
    const colorEntry = { color, mode, timestamp: Date.now() };
    const filtered = favorites.filter(
      (fav) => !(fav.color === color && fav.mode === mode)
    );
    filtered.unshift(colorEntry);
    const updatedFavorites = filtered.slice(0, 12);
    localStorage.setItem(FAVORITE_COLORS_KEY, JSON.stringify(updatedFavorites));
  } catch (error) {
    // pass
  }
};

const getFavoriteColorDisplay = (favorite) => {
  if (favorite.mode === "rgb") {
    return getDisplayColor(favorite.color, null, "rgb");
  } else if (favorite.mode === "temperature") {
    const temp = parseInt(favorite.color);
    return getDisplayColor(null, temp, "temperature");
  }
  return "#ffffff";
};

const OnOffBrightnessColorDetail1Widget = ({
  widget,
  onValueUpdate,
  pattern,
}) => {
  const getComponentBgColor = useComponentBackgroundColor();

  // On/Off state
  const [isOn, setIsOn] = useState(false);

  // Brightness state
  const [brightness, setBrightness] = useState(0);
  const [minMax, setMinMax] = useState({ min: 0, max: 100 });
  const lastBrightnessRef = useRef(null);

  // Color state
  const [colorRgb, setColorRgb] = useState("ffffff");
  const [colorTemp, setColorTemp] = useState(2700);
  const [current, setCurrent] = useState("rgb");
  const [tempMinMax, setTempMinMax] = useState({ min: 2700, max: 6500 });

  // Color picker state
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [favoriteColors, setFavoriteColors] = useState([]);
  const saveTimeoutRef = useRef(null);

  // Initialize states from widget data
  useEffect(() => {
    if (!widget) return;

    // On/Off state from $o1
    if (widget?.items?.$o1) {
      setIsOn(widget.items.$o1.value === "on");
    }

    // Brightness state from $b1
    if (widget?.items?.$b1) {
      const initialValue = Number(widget.items.$b1.value);
      const validValue = isNaN(initialValue) ? 0 : initialValue;
      setMinMax({
        min: widget.items.$b1.min ?? 0,
        max: widget.items.$b1.max ?? 100,
      });
      setBrightness(validValue);
      lastBrightnessRef.current = validValue;
    }

    // Color state from $c1, $c2, $c3
    if (widget.items?.$c1) {
      console.log("widget.items.$c1", widget.items.$c1);
      const colorValue = widget.items.$c1.value.toLowerCase() || "ffffff";
      setColorRgb(colorValue.replace(/^#/, ""));

      // Update hue and saturation sliders based on current RGB
      const hex = colorValue.replace(/^#/, "");
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      setHue(Math.round(h * 360));
      setSaturation(Math.round(s * 100));
    }

    if (widget.items?.$c2) {
      setTempMinMax({
        min: widget.items.$c2.min ?? 2700,
        max: widget.items.$c2.max ?? 6500,
      });
      setColorTemp(Number(widget.items.$c2.value) || 2700);
    }

    if (widget.items?.$c3) {
      setCurrent(widget.items.$c3.value || "rgb");
    } else {
      // If no mode selector, determine current based on available data
      if (widget.items?.$c1 && !widget.items?.$c2) {
        setCurrent("rgb");
      } else if (widget.items?.$c2 && !widget.items?.$c1) {
        setCurrent("temperature");
      }
    }
  }, [widget]);

  useEffect(() => {
    setFavoriteColors(getFavoriteColors());
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Get widget mode for color functionality
  const getWidgetMode = () => {
    if (!widget) return "all";

    const hasRgb = !!widget.items?.$c1;
    const hasTemp = !!widget.items?.$c2;

    if (!hasRgb && !hasTemp) {
      return "none";
    }

    const hasMode = !!widget.items?.$c3;

    if (hasRgb && hasTemp && hasMode) {
      return "all";
    } else if (hasRgb && !hasTemp) {
      return "rgb";
    } else if (hasTemp && !hasRgb) {
      return "temperature";
    } else {
      return "all";
    }
  };

  // Handle on/off toggle
  const handleToggle = () => {
    if (pattern && pattern.endsWith("_R")) return;

    const newValue = !isOn;
    setIsOn(newValue);

    const updatedWidget = {
      ...widget.items.$o1,
      value: newValue ? "on" : "off",
    };
    onValueUpdate(updatedWidget);
  };

  // Handle brightness change
  const handleBrightnessChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;

    event.stopPropagation();
    const adjustedValue = Math.min(Math.max(newValue, minMax.min), minMax.max);
    setBrightness(adjustedValue);
  };

  const handleBrightnessUpdate = (data) => {
    if (data === lastBrightnessRef.current) return;

    const updatedWidget = {
      ...widget.items.$b1,
      value: data,
    };
    lastBrightnessRef.current = data;
    onValueUpdate(updatedWidget);
  };

  // Handle color updates
  const handleColorValueUpdate = (data, type) => {
    if (!widget) return;
    const widgetMode = getWidgetMode();

    if (widgetMode === "rgb") {
      const updatedWidget = {
        ...widget.items.$c1,
        value: data.replace(/^#/, ""),
      };
      onValueUpdate([updatedWidget]);
    } else if (widgetMode === "temperature") {
      const updatedWidget = {
        ...widget.items.$c2,
        value: data.toString(),
      };
      onValueUpdate(updatedWidget);
    } else {
      const updatedWidgets = {};

      if (type === "$c1") {
        updatedWidgets.$c1 = { ...widget.items.$c1 };
        updatedWidgets.$c3 = { ...widget.items.$c3 };
        updatedWidgets.$c1.value = data.replace(/^#/, "");
        updatedWidgets.$c3.value = "rgb";
        setCurrent("rgb");
      } else if (type === "$c2") {
        updatedWidgets.$c2 = { ...widget.items.$c2 };
        updatedWidgets.$c3 = { ...widget.items.$c3 };
        updatedWidgets.$c2.value = data;
        updatedWidgets.$c3.value = "temperature";
        setCurrent("temperature");
      }

      onValueUpdate(Object.values(updatedWidgets));
    }
  };

  // RGB Color functions
  const updateRgbColor = (newHue, newSaturation) => {
    if (pattern && pattern.endsWith("_R")) return;

    // Convert HSL to Hex
    // Ensure saturation is at least 1 to avoid pure gray
    const effectiveSaturation = Math.max(newSaturation, 1);
    const h = newHue / 360;
    const s = effectiveSaturation / 100;
    const l = 0.5;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    const hexColor = `${toHex(r)}${toHex(g)}${toHex(b)}`;
    setColorRgb(hexColor);
    handleColorValueUpdate(hexColor, "$c1");
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveFavoriteColor(hexColor, "rgb");
      setFavoriteColors(getFavoriteColors());
    }, 300);
  };

  const handleHueChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    setHue(newValue);
  };

  const handleHueChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    updateRgbColor(newValue, saturation);
  };

  const handleSaturationChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    setSaturation(newValue);
  };

  const handleSaturationChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    updateRgbColor(hue, newValue);
  };

  // Temperature functions
  const handleTemperatureChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    setColorTemp(newValue);
  };

  const handleTemperatureChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    handleColorValueUpdate(newValue, "$c2");
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveFavoriteColor(newValue.toString(), "temperature");
      setFavoriteColors(getFavoriteColors());
    }, 300);
  };

  const handleFavoriteColorClick = (favorite) => {
    if (pattern && pattern.endsWith("_R")) return;
    if (!favorite) return;
    if (favorite.mode === "rgb") {
      const hex = favorite.color.startsWith("#")
        ? favorite.color.substring(1)
        : favorite.color;
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = max === 0 ? 0 : d / max;
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }
      setHue(Math.round(h * 360));
      setSaturation(Math.round(s * 100));
      setColorRgb(hex);
      handleColorValueUpdate(hex, "$c1");
    } else if (favorite.mode === "temperature") {
      const temp = parseInt(favorite.color);
      setColorTemp(temp);
      handleColorValueUpdate(temp, "$c2");
    }
  };

  // Get current color for display
  const getCurrentColor = () => {
    const widgetMode = getWidgetMode();
    const mode =
      widgetMode === "rgb" || current === "rgb" ? "rgb" : "temperature";

    if (widgetMode === "none") {
      return isOn ? "#FFD700" : "#B0B0B0";
    }

    return getDisplayColor(colorRgb || "ffffff", colorTemp, mode);
  };

  const disabled = pattern && pattern.endsWith("_R");
  const showWarning = pattern && pattern.endsWith("_W");
  const widgetMode = getWidgetMode();

  // Generate temperature gradient
  const gradientStops = [];
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const temp =
      tempMinMax.min + (i / steps) * (tempMinMax.max - tempMinMax.min);
    const rgb = kelvinToRGB(temp);
    gradientStops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
  }

  const sliderStyles = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "100%",
    "& .MuiSlider-thumb": {
      width: 4,
      height: 20,
      borderRadius: 16,
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      "&:hover, &.Mui-active": {
        boxShadow: "0 1px 4px rgba(0,0,0,0.6)",
      },
      "&:before": {
        display: "none",
      },
    },
    "& .MuiSlider-track": {
      height: 25,
      border: "none",
      borderRadius: 16,
      backgroundColor: "transparent",
    },
    "& .MuiSlider-rail": {
      height: 25,
      borderRadius: 16,
      backgroundColor: "transparent",
    },
  };

  const isRgbActive = current === "rgb";
  const isTempActive = current === "temperature";
  const showHue = widgetMode === "all" || widgetMode === "rgb";
  const showSaturation = widgetMode === "all" || widgetMode === "rgb";
  const showTemperature = widgetMode === "all" || widgetMode === "temperature";

  const foreground = isOn ? getContrastTextColor(getCurrentColor()) : "#333333";

  return (
    <div className="w-full flex flex-col gap-6">
      {/* On/Off Card */}
      <div
        className="rounded-2xl p-6 flex items-center justify-between h-[60px] transition-all duration-300"
        style={{
          background: isOn
            ? `linear-gradient(135deg, ${getCurrentColor()} 0%, rgba(255,255,255,0.4) 100%)`
            : `linear-gradient(135deg, #B0B0B0 80%, #E0E0E0 100%)`,
          opacity: isOn ? 1 : 0.3,
        }}
      >
        <Typography
          className="font-bold flex items-center gap-2"
          style={{ color: foreground }}
        >
          {isOn ? "ON" : "OFF"}
          {showWarning && (
            <SyncProblemIcon fontSize="inherit" style={{ color: foreground }} />
          )}
        </Typography>
        <div
          className="h-10 aspect-square flex items-center justify-center flex-shrink-0"
          style={{
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.8 : 1,
            WebkitTapHighlightColor: "transparent",
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
          }}
        >
          <CirclePower
            size="100%"
            color={foreground}
            fill={foreground}
            opacity={isOn ? 1 : 0.8}
            fillOpacity={0.2}
            strokeWidth={2}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </div>
      </div>

      {/* Brightness Card */}
      <div className="h-12">
        <BrightnessBar
          value={brightness}
          onChange={handleBrightnessChange}
          disabled={disabled}
          onValueUpdate={handleBrightnessUpdate}
          min={minMax.min}
          max={minMax.max}
          trackColor={getCurrentColor()}
          opacity={isOn ? 1 : 0.3}
          showLevel={true}
          showText="Brightness"
          showWarningIcon={showWarning}
          textColor={getContrastTextColor(getCurrentColor())}
        />
      </div>

      {/* Color Section */}
      {widgetMode !== "none" && (
        <div
          className="flex flex-col rounded-2xl p-6"
          style={{
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
          }}
        >
          {/* RGB Controls */}
          {showHue && (
            <div className="mb-8" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
              <Typography className="mb-4 font-bold flex items-center gap-2">
                Hue - {hue}°
                {showWarning && (
                  <SyncProblemIcon
                    fontSize="inherit"
                    style={{ color: "hsl(var(--warning))" }}
                  />
                )}
              </Typography>
              <div className="relative h-9">
                <div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                  }}
                />
                <Slider
                  value={hue}
                  onChange={handleHueChange}
                  onChangeCommitted={handleHueChangeCommitted}
                  min={0}
                  max={360}
                  step={1}
                  disabled={disabled}
                  sx={sliderStyles}
                />
              </div>
            </div>
          )}

          {showSaturation && (
            <div className="mb-8" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
              <Typography className="mb-4 font-bold flex items-center gap-2">
                Saturation - {saturation}%
                {showWarning && (
                  <SyncProblemIcon
                    fontSize="inherit"
                    style={{ color: "hsl(var(--warning))" }}
                  />
                )}
              </Typography>
              <div className="relative h-9">
                <div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    background: `linear-gradient(to right, hsl(${hue}, 0%, 100%) 0%, hsl(${hue}, 100%, 50%) 100%)`,
                  }}
                />
                <Slider
                  value={saturation}
                  onChange={handleSaturationChange}
                  onChangeCommitted={handleSaturationChangeCommitted}
                  min={1}
                  max={100}
                  step={1}
                  disabled={disabled}
                  sx={sliderStyles}
                />
              </div>
            </div>
          )}

          {/* Temperature Control */}
          {showTemperature && (
            <div style={{ opacity: isTempActive ? 1 : 0.4 }}>
              <Typography className="mb-4 font-bold flex items-center gap-2">
                Color Temperature - {colorTemp}K
                {showWarning && (
                  <SyncProblemIcon
                    fontSize="inherit"
                    style={{ color: "hsl(var(--warning))" }}
                  />
                )}
              </Typography>
              <div className="relative h-9">
                <div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    background: `linear-gradient(to right, ${gradientStops.join(
                      ", "
                    )})`,
                  }}
                />
                <Slider
                  value={colorTemp}
                  onChange={handleTemperatureChange}
                  onChangeCommitted={handleTemperatureChangeCommitted}
                  min={tempMinMax.min}
                  max={tempMinMax.max}
                  step={50}
                  disabled={disabled}
                  sx={sliderStyles}
                />
              </div>
            </div>
          )}
          {favoriteColors.length > 0 && (
            <div className="w-full mt-4">
              <Typography className="mb-4 font-bold flex items-center gap-2">
                Recent Colors
              </Typography>
              <div className="flex w-full items-center flex-wrap gap-8">
                {favoriteColors.map((favorite, index) => (
                  <div
                    key={`${favorite.color}-${favorite.mode}-${index}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFavoriteColorClick(favorite);
                    }}
                    className="w-6 h-6 rounded-full border transition-transform duration-200"
                    style={{
                      backgroundColor: getFavoriteColorDisplay(favorite),
                      cursor: disabled ? "default" : "pointer",
                      border: "1px solid rgba(192, 192, 192, 0.65)",
                      opacity: disabled ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.transform = "scale(1.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    onMouseDown={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.transform = "scale(0.95)";
                      }
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnOffBrightnessColorDetail1Widget;
